import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IAutomationActionTemplateGoogleAddToGroup,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import { GoogleGroupDescription } from '@dadohr/shared/domainTypes/integrations/googleSheets';
import { isEqual } from 'lodash';
import { SetStateAction, useMemo } from 'react';
import { AlertBox } from '../../../../../../../components/AlertBox';
import { AutocompleteSingle } from '../../../../../../../components/Autocomplete';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../../components/InfoBox';
import { Loader } from '../../../../../../../components/Loader';
import { NotFound } from '../../../../../../../components/NotFound';
import { SomethingWentWrong } from '../../../../../../../components/SomethingWentWrong';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { useCurrentUser } from '../../../../../../../services/currentUser';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import { toSearchTermRegexp } from '../../../../../../../services/user';
import { useGroupList } from '../../../../../../Org/pages/Integrations/integrations/googleSheets/groups';
import { useGoogleSheetsCurrentUser } from '../../../../../../Org/pages/Integrations/integrations/googleSheets/service';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import { ActionActorSelectorMulti, actionActorToString } from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  useFieldValidator,
  VALIDATORS_GOOGLE_ADD_TO_GROUP
} from '../../validation';

const getEmailDomain = (emailAddress: string) =>
  emailAddress.substring(emailAddress.indexOf('@') + 1);

const GroupSelector = ({
  error,
  helperText,
  onBlur,
  onChange,
  value,
  groups
}: {
  error?: boolean;
  helperText?: string | null;
  integrationEmail: string;
  onChange: (nextValue: GoogleGroupDescription) => void;
  onBlur: () => void;
  value: {
    id: string;
    name: string;
  };
  groups: GoogleGroupDescription[];
}) => {
  const { t } = useUnsafeTranslation();

  const sortedOptions = groups.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <AutocompleteSingle
      fullWidth={true}
      value={value}
      label={t('Which group should the member be added to')}
      options={sortedOptions}
      getOptionLabel={(o) => o.name}
      getOptionSelected={(o, v) => o.id === v.id}
      helperText={helperText}
      error={!!error}
      required
      onChange={(groupNext) => groupNext && onChange(groupNext)}
      onBlur={() => onBlur()}
      renderOption={(o: GoogleGroupDescription) => <>{o.name}</>}
      filterOptions={(os, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => t.exec(o.name));
      }}
    />
  );
};

export const ActionSettingsGoogleAddToGroupInner = ({
  expTpl,
  onChange,
  setValidation,
  validation,
  value,
  aTpl,
  googleIntegrationUserEmail
}: {
  value: IAutomationActionTemplateGoogleAddToGroup;
  onChange: (nextValue: IAutomationActionTemplateGoogleAddToGroup) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  googleIntegrationUserEmail: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();

  const domain = getEmailDomain(googleIntegrationUserEmail);
  const [groups, loading, error] = useGroupList(org.id, domain);

  const validatePplToAdd = useFieldValidator(
    setValidation,
    VALIDATORS_GOOGLE_ADD_TO_GROUP,
    'pplToAdd'
  );

  const validateGroup = useFieldValidator(
    setValidation,
    VALIDATORS_GOOGLE_ADD_TO_GROUP,
    'group'
  );

  const invalidPpltoAdd = validation?.fields.pplToAdd || null;
  const invalidGroup = validation?.fields.group || null;

  const groupsLoadErrorMessage = useMemo(() => {
    if (!error) {
      return null;
    }

    switch (error?.message) {
      case 'INSUFFICIENT_PRIVILEGES':
        return t(
          'To use this automation type, {{ emailAddress }} needs additional admin privileges in your Google Workspace. Please grant them either the Groups Admin role, or both Groups Reader and Groups Editor roles.',
          { emailAddress: googleIntegrationUserEmail }
        );
      default:
        return t('Something went wrong. Please try again.');
    }
  }, [t, error, googleIntegrationUserEmail]);

  if (loading) {
    return <Loader />;
  }

  if (!googleIntegrationUserEmail) {
    // TODO: check whether this should never happen
    return <SomethingWentWrong height={150} />;
  }

  return (
    <div>
      <FormRow>
        <ActionActorSelectorMulti
          error={!!invalidPpltoAdd}
          expTpl={expTpl}
          fullWidth
          helperText={invalidPpltoAdd}
          label={t('Who should be added')}
          value={value.pplToAdd.filter((p) => p.type === 'actor')}
          onBlur={() => validatePplToAdd(value, expTpl, aTpl)}
          onChange={(nextPplToAdd) => {
            // artificially split to only contain actors, so we need to recompose
            const nextValue = {
              ...value,
              pplToAdd: nextPplToAdd.concat(
                value.pplToAdd.filter((p) => p.type === 'other')
              )
            };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
          filterOptions={(options) =>
            options.filter((a) => !value.pplToAdd.some((p) => isEqual(p, a)))
          }
        />
      </FormRow>
      <FormRow>
        <GroupSelector
          integrationEmail={googleIntegrationUserEmail}
          groups={groups || EMPTY_ARR}
          error={!!(invalidGroup || groupsLoadErrorMessage)}
          helperText={invalidGroup || groupsLoadErrorMessage}
          value={value.group}
          onBlur={() => validateGroup(value, expTpl, aTpl)}
          onChange={(nextGroup) => {
            const nextValue = { ...value, group: nextGroup };
            invalidGroup && validateGroup(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
        />
      </FormRow>
      <AlertBox color="yellow">
        {t(
          'If your organization has more than 500 groups, it can take up to 20 seconds to load the full list of groups.'
        )}
      </AlertBox>
    </div>
  );
};

export const ActionSettingsGoogleAddToGroup = (props: {
  value: IAutomationActionTemplateGoogleAddToGroup;
  onChange: (nextValue: IAutomationActionTemplateGoogleAddToGroup) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const [googleIntegrationUser, loading, error] = useGoogleSheetsCurrentUser(
    org.id
  );

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <SomethingWentWrong height={150} />;
  }

  if (!googleIntegrationUser) {
    return <NotFound height={150} />;
  }

  if (!googleIntegrationUser.email) {
    // TODO: check whether this should never happen
    return <SomethingWentWrong height={150} />;
  }

  if (
    !googleIntegrationUser.scopes.includes(
      'https://www.googleapis.com/auth/admin.directory.group'
    )
  ) {
    return (
      <InfoBox color="yellow">
        {t(
          'To use this automation type, you need to update your Google Workplace integration. Please remove and immediately re-add this integration, in the Organization tab.'
        )}
      </InfoBox>
    );
  }

  return (
    <ActionSettingsGoogleAddToGroupInner
      {...props}
      googleIntegrationUserEmail={googleIntegrationUser.email}
    />
  );
};

export const ActionSettingsGoogleAddToGroupReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateGoogleAddToGroup;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Who should be added')}>
          {joinWithCommaAndAnd(
            value.pplToAdd.map((p) =>
              actionActorToString(p, expTpl.supportingActors, t)
            ),
            t
          )}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel
          label={t('Which group should the member be added to')}
        >
          {value.group.name}
        </DataFieldWithLabel>
      </FormRow>
    </div>
  );
};
